import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from '../UserContext';
import ToolForm from '../components/ToolForm';

const EditToolPage = () => {
  const { toolId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const [tool, setTool] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Memoize the user object
  const memoizedUser = useMemo(() => user, [user?.uid]);

  useEffect(() => {
    console.log('EditToolPage useEffect triggered');
    console.log('toolId:', toolId);
    console.log('user:', memoizedUser);

    const fetchToolAndCategories = async () => {
      if (!memoizedUser) {
        console.log('User not logged in');
        setError("Please log in to edit your tool.");
        setLoading(false);
        return;
      }

      try {
        // Fetch tool data
        const toolRef = doc(db, 'tools', toolId);
        const toolSnap = await getDoc(toolRef);

        if (toolSnap.exists()) {
          const toolData = toolSnap.data();
          if (toolData.ownerId !== memoizedUser.uid) {
            console.log('User does not have permission to edit this tool');
            setError("You don't have permission to edit this tool.");
          } else {
            console.log('Tool data fetched successfully');
            setTool({ id: toolSnap.id, ...toolData });
          }
        } else {
          console.log('Tool not found');
          setError("Tool not found.");
        }

        // Fetch categories
        const categoriesCollection = collection(db, 'toolCategories');
        const categoriesSnapshot = await getDocs(categoriesCollection);
        const categoriesData = categoriesSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name
        }));
        console.log('Categories fetched:', categoriesData);
        setCategories(categoriesData);

      } catch (err) {
        console.error("Error fetching data: ", err);
        setError("Failed to load the tool or categories. Please try again later.");
      }

      setLoading(false);
    };

    fetchToolAndCategories();
  }, [toolId, memoizedUser]);

  const handleImageDelete = async (updatedImageUrls) => {
    try {
      const toolRef = doc(db, 'tools', toolId);
      await updateDoc(toolRef, { imageUrls: updatedImageUrls });
      setTool(prevTool => ({ ...prevTool, imageUrls: updatedImageUrls }));
    } catch (err) {
      console.error("Error updating tool images: ", err);
      setError("Failed to update tool images. Please try again.");
    }
  };

  const handleSubmit = async (updatedToolData) => {
    setError(null);
    console.log("Attempting to update tool with data:", JSON.stringify(updatedToolData, null, 2));
    
    try {
      const toolRef = doc(db, 'tools', toolId);
      
      console.log("Updating document in Firestore...");
      await updateDoc(toolRef, updatedToolData);
      
      console.log("Tool updated successfully");
      navigate('/my-tools');
    } catch (err) {
      console.error("Error updating tool: ", err);
      
      let errorMessage = "Failed to update the tool. ";
      if (err.code) {
        errorMessage += `Error code: ${err.code}. `;
      }
      if (err.message) {
        errorMessage += `Error message: ${err.message}. `;
      }
      errorMessage += "Please try again.";
      
      setError(errorMessage);
    }
  };

  if (loading) {
    return <div className="text-center mt-8">Loading...</div>;
  }

  if (error) {
    return <div className="text-center mt-8 text-red-500">{error}</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Edit Tool</h1>
      {tool && categories.length > 0 && (
        <ToolForm 
          initialData={tool} 
          categories={categories} 
          onSubmit={handleSubmit}
          onImageDelete={handleImageDelete}
        />
      )}
    </div>
  );
};

export default EditToolPage;
